import {
  collection, doc, onSnapshot, updateDoc, query, orderBy, Unsubscribe, setDoc, addDoc, getDoc, runTransaction,
} from 'firebase/firestore';
import { db } from './firebaseService';
import { TIER_PRIVILEGES } from '../constants';
import { UserMessage, UserInboxMeta } from '../types';

/**
 * Real-time subscription to a user's inbox messages, newest first.
 */
export function subscribeToUserMessages(
  uid: string,
  onMessages: (messages: UserMessage[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'userMessages', uid, 'messages'),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(q, (snap) => {
    const msgs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as UserMessage));
    onMessages(msgs);
  }, () => onMessages([]));
}

/**
 * Real-time subscription to the inbox metadata document (unread count).
 */
export function subscribeToUserInboxMeta(
  uid: string,
  onMeta: (meta: UserInboxMeta | null) => void,
): Unsubscribe {
  return onSnapshot(doc(db, 'userMessages', uid), (snap) => {
    onMeta(snap.exists() ? (snap.data() as UserInboxMeta) : null);
  }, () => onMeta(null));
}

/**
 * Mark a single message as read.
 */
export async function markMessageAsRead(uid: string, messageId: string): Promise<void> {
  const messageRef = doc(db, 'userMessages', uid, 'messages', messageId);
  const metaRef = doc(db, 'userMessages', uid);

  await runTransaction(db, async (transaction) => {
    const messageSnap = await transaction.get(messageRef);
    if (!messageSnap.exists()) return;

    const messageData = messageSnap.data();
    if (messageData.isRead) return;

    transaction.update(messageRef, { isRead: true });

    const metaSnap = await transaction.get(metaRef);
    if (metaSnap.exists()) {
      const currentUnread = metaSnap.data().unreadCount || 0;
      transaction.update(metaRef, {
        unreadCount: Math.max(0, currentUnread - 1),
        lastUpdatedAt: Date.now(),
      });
    }
  });
}

/**
 * Send an inbox message when a user levels up.
 */
export async function sendLevelUpInboxMessage(uid: string, newTierName: string, newTierLevel: number): Promise<void> {
  const unlockDescription = TIER_PRIVILEGES[newTierLevel]?.descriptionNo;
  const collectionRef = collection(db, 'userMessages', uid, 'messages');
  await addDoc(collectionRef, {
    title: `Gratulerer! Du er nå ${newTierName}!`,
    body: unlockDescription
      ? `Du har låst opp nivå ${newTierLevel}: ${unlockDescription}. Fortsett å sjekke inn og verifisere steder for å nå neste nivå.`
      : `Du har låst opp nivå ${newTierLevel}. Fortsett å sjekke inn og verifisere steder for å nå neste nivå.`,
    isRead: false,
    createdAt: Date.now(),
    type: 'system',
    actionUrl: '#visits'
  });

  // Increment unread count
  const metaRef = doc(db, 'userMessages', uid);
  const metaSnap = await getDoc(metaRef);

  if (metaSnap.exists()) {
    const currentUnread = metaSnap.data().unreadCount || 0;
    await updateDoc(metaRef, { unreadCount: currentUnread + 1, lastUpdatedAt: Date.now() });
  } else {
    await setDoc(metaRef, { unreadCount: 1, lastUpdatedAt: Date.now() });
  }
}
